import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuClock9 } from "react-icons/lu";
import { FaHeart } from "react-icons/fa";
import { useNavigate } from "react-router";

import { onUnsavePress } from "@/api/srpFilter";
import imageOnTheWay from "../../../../public/assets/images/imageOnTheWay.webp";
import Timer from "../../common/header/save-preference/Timer";
import BidText from "../../common/header/save-preference/BidText";
import BidButton from "../../common/header/save-preference/BidButton";
import PriceAndLabel from "@/components/srp/CardComponents/PriceAndLabel";
import {
  getEventCategory,
  trackApiCall,
  trackButtonClick,
} from "@/utils/analytics";
import { isPhoneNoVerified } from "@/api/bid";
import { checkLoginStatus } from "@/api/myProfile";
import { getBidCenterDetails, getPDPInitData } from "@/api/PDP";
import {
  enableOwnItNow,
  fetchPDPBidCenterDataSuccess,
  fetchPDPInitialLoadSuccess,
  fetchPDPLatestBidCenterData,
  setSelectedBidAmount,
} from "@/redux/PDP/pdpSlice";
import { setListingId } from "@/bidFeature/propertyDetailSlice";
import { navigateToProperty } from "@/utils/propertyNavigation";
import { markInternalNavigation } from "@/utils/attributionCapture";

const SavedPropertyCard = ({ item, cardIndex, properties, setProperties }) => {
  const user = useSelector((state) => state.user);
  const [hasError, setHasError] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const removeFromFavs = async (propertyData) => {
    const propertyId = propertyData?.propertyDetails?.propertyId;
    const listingId = propertyData?.propertyListingDetails?.listingId;
    try {
      let eventCategory = getEventCategory();

      trackButtonClick({
        eventName: "favourite_property_unsave_click",
        eventCategory: `${eventCategory} - Save List Slider`,
        eventSource: "buyer_dashboard_save_list",
        eventLabel: `Property Id : ${propertyId} | listingId : ${listingId} | propertyStatus : SCHEDULED`,
      });

      const response = await onUnsavePress({ propertyId: propertyId });

      if (response === "success") {
        trackApiCall({
          eventName: "favourite_property_unsaved",
          eventCategory: `${eventCategory} - Save List Slider`,
          eventSource: "buyer_dashboard_save_list",
          eventLabel: `Property Id : ${propertyId}`,
        });

        // Remove from local state
        const updatedProperties = properties.filter(
          (prop) => prop?.propertyDetails?.propertyId !== propertyId
        );
        setProperties(updatedProperties);
      } else {
        trackApiCall({
          eventName: "favourite_property_unsave_error",
          eventCategory: `Error - unable to delete`,
          eventSource: "buyer_dashboard_save_list",
          eventLabel: `Property Id : ${propertyId}`,
        });
      }
    } catch (error) {
      console.error("Error removing from favorites:", error);
    }
  };

  const imageUrl = (img) => {
    return hasError || !img
      ? imageOnTheWay
      : img.indexOf("https") > -1
      ? img
      : imageOnTheWay;
  };

  const onNavigatePDP = (propertyDetails, source = "general") => {
    const id = propertyDetails?.propertyId;
    let eventCategory = getEventCategory();

    markInternalNavigation()

    trackButtonClick({
      eventName: "save_list_pdp_navigate",
      eventCategory: `${eventCategory}`,
      eventSource: "Property image click",
      eventLabel: `Property Id : ${id} | Source: Save List Drawer`,
      eventParams: { source: source === "button" ? "button" : "image" },
    });
    navigateToProperty(`/property/${id}`, propertyDetails?.commonAttributes?.propCategory, propertyDetails?.commonAttributes?.status);
  };

  const handleEndAuctionClick = async (propertyData) => {
    const propertyId = propertyData?.propertyDetails?.propertyId;
    const listingId = propertyData?.propertyListingDetails?.listingId;

    try {
      let eventCategory = getEventCategory();

      trackButtonClick({
        eventName: "user_type_check",
        eventCategory: eventCategory,
        eventSource: "End auction click",
      });

      const loginResponse = await checkLoginStatus();

      if (loginResponse) {
        const isPhoneVerifiedResponse = await isPhoneNoVerified(
          loginResponse.primaryPhone
        );

        trackApiCall({
          eventName: "user_type_check_success",
          eventCategory: eventCategory,
          eventSource: "End auction click api call",
          eventParams: { user_type_check_result: true },
        });

        trackApiCall({
          eventName: "own_it_now",
          eventCategory: eventCategory,
          eventSource: "End auction api call",
          eventLabel: `listingId: ${listingId} | Source: Save List Drawer`,
        });

        const data = await getBidCenterDetails(listingId);
        const details = await getPDPInitData(listingId);
        dispatch(fetchPDPBidCenterDataSuccess(data));
        dispatch(fetchPDPLatestBidCenterData(listingId));
        dispatch(fetchPDPInitialLoadSuccess(details));
        dispatch(setListingId(propertyData?.propertyListingDetails));
        dispatch(setSelectedBidAmount(null));
        dispatch(enableOwnItNow(true));

        document.body.style.overflow = "";
        document.body.style.padding = "";
        navigate("/bid");
      }
    } catch (error) {
      console.log("Error in handleEndAuctionClick:", error);
      trackApiCall({
        eventName: "own_it_now",
        eventCategory: "Error - handleEndAuctionClick",
        eventSource: "End auction api error",
      });
    }
  };

  return (
    <div id={`saved-property-card-${cardIndex}`} className="property-saved">
      <div id={`saved-property-image-container-${cardIndex}`} className="image-div">
        <img
          src={imageUrl(
            item?.propertyDetails?.imageDetails[0]?.imageStoragePath
          )}
          id={`saved-property-image-${cardIndex}`}
          onError={() => setHasError(true)}
          alt="property"
          className="preference-property-image"
          onClick={() => onNavigatePDP(item?.propertyDetails)}
        />
        <div id={`saved-property-fav-container-${cardIndex}`} className="fav-icons">
          <FaHeart id={`saved-property-fav-icon-${cardIndex}`} color="#ff5a5f" onClick={() => removeFromFavs(item)} />
        </div>
      </div>

      <div id={`saved-property-data-${cardIndex}`} className="right-data-container">
        <div id={`saved-property-bid-data-${cardIndex}`} className="bid-data">
          <BidText data={item} />
          {item && <PriceAndLabel item={item} />}
          {item?.propertyListingDetails?.listingEndingInfo && (
            <span id={`saved-property-timer-${cardIndex}`} className="countdown-timer">
              <LuClock9 />
              <Timer
                initialTime={
                  item?.propertyListingDetails?.listingEndingInfo
                }
              />
            </span>
          )}
        </div>

        <p
          id={`saved-property-address-${cardIndex}`}
          className="address"
          onClick={() => onNavigatePDP(item?.propertyDetails)}
        >
          {item?.propertyDetails?.address?.streetNumber}{" "}
          {item?.propertyDetails?.address?.streetName}
        </p>

        <div id={`saved-property-buttons-${cardIndex}`} className="preference-button">
          <BidButton
            propertyDetails={item?.propertyDetails}
            propertyListingDetails={item?.propertyListingDetails}
            cifCompleted={item?.cifCompleted}
            fcProxyBidEnabled={item?.fcProxyBidEnabled}
            isBestBidAvailable={item?.isBestBidAvailable}
            isHnbEnded={item?.isHnbEnded}
            sellerFinalNegotiationResponse={
              item?.sellerFinalNegotiationResponse
            }
            userRegisteredForFcProxyBid={
              item?.userRegisteredForFcProxyBid
            }
            userId={item?.userId}
            isPhoneNumberVerified={user?.user?.phoneNumberVerified}
          />
          <button
            id={`saved-property-view-details-${cardIndex}`}
            className="btn btn-sm hubzu-btn-thm"
            onClick={() =>
              onNavigatePDP(item?.propertyDetails, "button")
            }
          >
            View Details
          </button>
        </div>

        {(item?.propertyListingDetails?.ownItNowFlag && item?.propertyDetails?.commonAttributes?.status !== "REVIEW") && (
          <>
            <p id={`saved-property-or-text-${cardIndex}`} className="text-center">OR</p>
            <div id={`saved-property-end-auction-section-${cardIndex}`} className="preference-button">
              <button
                id={`saved-property-end-auction-${cardIndex}`}
                className="btn btn-sm btn-thm"
                onClick={() => handleEndAuctionClick(item)}
              >
                End Auction Now
              </button>
              for ${item?.propertyListingDetails?.ownItNowPrice}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SavedPropertyCard;